import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import type { Expense } from "../expenses/types";
import type { Invoice } from "../invoices/types";
import type { ExportDataSources } from "./types";

// Rows per page for the table body. Tuned for A4 + 10pt rows; keeps the
// footer from colliding with body content. If the layout changes this value
// should be revisited.
const ROWS_PER_PAGE = 40;

// Shared typography. We stick to the built-in Helvetica family so the build
// doesn't have to ship font files — the PDFs remain fully self-contained and
// lightweight.
const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 48,
    paddingHorizontal: 32,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#1f2937",
  },
  header: {
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#cbd5e1",
    borderBottomStyle: "solid",
    paddingBottom: 8,
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    color: "#0f172a",
  },
  headerSub: {
    fontSize: 10,
    color: "#475569",
    marginTop: 2,
  },
  headerMeta: {
    fontSize: 9,
    color: "#64748b",
    marginTop: 4,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: "#e2e8f0",
    borderBottomStyle: "solid",
  },
  tableHeaderRow: {
    flexDirection: "row",
    paddingVertical: 5,
    backgroundColor: "#f1f5f9",
    borderBottomWidth: 1,
    borderBottomColor: "#94a3b8",
    borderBottomStyle: "solid",
  },
  th: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#0f172a",
    paddingHorizontal: 4,
  },
  td: {
    fontSize: 9,
    color: "#1f2937",
    paddingHorizontal: 4,
  },
  right: {
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    left: 32,
    right: 32,
    bottom: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 8,
    color: "#94a3b8",
  },
  emptyState: {
    marginTop: 24,
    fontSize: 10,
    color: "#64748b",
    fontStyle: "italic",
  },
});

function centsToDisplay(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}${dollars}.${remainder.toString().padStart(2, "0")}`;
}

function chunk<T>(rows: T[], size: number): T[][] {
  if (rows.length === 0) return [[]];
  const out: T[][] = [];
  for (let i = 0; i < rows.length; i += size) {
    out.push(rows.slice(i, i + size));
  }
  return out;
}

function describeDateRange(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  if (!from && !to) return "All dates";
  if (from && to) return `${from} to ${to}`;
  if (from) return `From ${from}`;
  return `Through ${to}`;
}

export interface GenerateExpensesPdfOptions {
  expenses: Expense[];
  categories: ExportDataSources["categories"];
  taxPolicies: ExportDataSources["taxPolicies"];
  orgName: string;
  dateRange: {
    from: string | null | undefined;
    to: string | null | undefined;
  };
}

function categoryLabelFor(
  expense: Expense,
  categories: ExportDataSources["categories"],
): string {
  if (expense.categoryLabel) return expense.categoryLabel;
  if (!expense.categoryId) return "";
  const match = categories.find((c) => c.id === expense.categoryId);
  return match?.label ?? "";
}

function taxPolicyLabelFor(
  expense: Expense,
  taxPolicies: ExportDataSources["taxPolicies"],
): string {
  if (expense.taxPolicyLabel) return expense.taxPolicyLabel;
  if (!expense.taxPolicyId) return "";
  const match = taxPolicies.find((p) => p.id === expense.taxPolicyId);
  return match?.category ?? "";
}

// Column widths tuned for A4 with 32pt horizontal padding. They sum to ~100
// so flex-basis lines up the columns neatly across both rows and headers.
const EXPENSE_COLS = {
  date: { width: "14%" },
  vendor: { width: "22%" },
  category: { width: "18%" },
  amount: { width: "14%" },
  tax: { width: "14%" },
  deductible: { width: "18%" },
} as const;

function ExpensesHeader({
  orgName,
  dateRange,
  rowCount,
}: {
  orgName: string;
  dateRange: { from: string | null | undefined; to: string | null | undefined };
  rowCount: number;
}) {
  return (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>{orgName || "Proofly"}</Text>
      <Text style={styles.headerSub}>Expense Report</Text>
      <Text style={styles.headerMeta}>
        {describeDateRange(dateRange.from, dateRange.to)} · {rowCount} expense
        {rowCount === 1 ? "" : "s"}
      </Text>
    </View>
  );
}

function ExpensesTableHeader() {
  return (
    <View style={styles.tableHeaderRow} fixed>
      <Text style={[styles.th, EXPENSE_COLS.date]}>Date</Text>
      <Text style={[styles.th, EXPENSE_COLS.vendor]}>Vendor</Text>
      <Text style={[styles.th, EXPENSE_COLS.category]}>Category</Text>
      <Text style={[styles.th, EXPENSE_COLS.amount, styles.right]}>Amount</Text>
      <Text style={[styles.th, EXPENSE_COLS.tax, styles.right]}>Tax</Text>
      <Text style={[styles.th, EXPENSE_COLS.deductible, styles.right]}>
        Deductible
      </Text>
    </View>
  );
}

function PageFooter() {
  return (
    <View style={styles.footer} fixed>
      <Text>Generated by Proofly</Text>
      <Text
        render={({ pageNumber, totalPages }) =>
          `Page ${pageNumber} of ${totalPages}`
        }
      />
    </View>
  );
}

export async function generateExpensesPdf(
  options: GenerateExpensesPdfOptions,
): Promise<Blob> {
  const { expenses, categories, taxPolicies, orgName, dateRange } = options;

  const rows = expenses.map((e) => {
    const taxLabel = taxPolicyLabelFor(e, taxPolicies);
    const taxValue =
      e.taxAmountCents !== null && e.taxAmountCents !== undefined
        ? `${centsToDisplay(e.taxAmountCents)} ${e.currency}`
        : "";
    return {
      date: e.occurredOn,
      vendor: e.vendor,
      category: categoryLabelFor(e, categories),
      amount: `${centsToDisplay(e.amountCents)} ${e.currency}`,
      // Include the policy label alongside the amount when both are present
      // — gives auditors the "why" behind the number without adding another
      // column.
      tax: taxLabel && taxValue ? `${taxValue} (${taxLabel})` : taxValue,
      deductible:
        e.deductibleAmountCents !== null
          ? `${centsToDisplay(e.deductibleAmountCents)} ${e.currency}`
          : "",
    };
  });

  const pages = chunk(rows, ROWS_PER_PAGE);

  const doc = (
    <Document
      title={`Proofly expenses ${describeDateRange(dateRange.from, dateRange.to)}`}
      author="Proofly"
      creator="Proofly"
      producer="Proofly"
    >
      {pages.map((pageRows, pageIdx) => (
        <Page key={pageIdx} size="A4" style={styles.page} wrap>
          {pageIdx === 0 ? (
            <ExpensesHeader
              orgName={orgName}
              dateRange={dateRange}
              rowCount={rows.length}
            />
          ) : null}
          <ExpensesTableHeader />
          {pageRows.length === 0 ? (
            <Text style={styles.emptyState}>
              No expenses matched the selected filters.
            </Text>
          ) : (
            pageRows.map((row, idx) => (
              <View key={idx} style={styles.tableRow} wrap={false}>
                <Text style={[styles.td, EXPENSE_COLS.date]}>{row.date}</Text>
                <Text style={[styles.td, EXPENSE_COLS.vendor]}>
                  {row.vendor}
                </Text>
                <Text style={[styles.td, EXPENSE_COLS.category]}>
                  {row.category}
                </Text>
                <Text style={[styles.td, EXPENSE_COLS.amount, styles.right]}>
                  {row.amount}
                </Text>
                <Text style={[styles.td, EXPENSE_COLS.tax, styles.right]}>
                  {row.tax}
                </Text>
                <Text
                  style={[styles.td, EXPENSE_COLS.deductible, styles.right]}
                >
                  {row.deductible}
                </Text>
              </View>
            ))
          )}
          <PageFooter />
        </Page>
      ))}
    </Document>
  );

  return pdf(doc).toBlob();
}

export interface GenerateInvoicesPdfOptions {
  invoices: Invoice[];
  orgName: string;
  dateRange: {
    from: string | null | undefined;
    to: string | null | undefined;
  };
}

const INVOICE_COLS = {
  issued: { width: "14%" },
  counterparty: { width: "26%" },
  direction: { width: "12%" },
  amount: { width: "16%" },
  due: { width: "14%" },
  status: { width: "18%" },
} as const;

function InvoicesTableHeader() {
  return (
    <View style={styles.tableHeaderRow} fixed>
      <Text style={[styles.th, INVOICE_COLS.issued]}>Issued</Text>
      <Text style={[styles.th, INVOICE_COLS.counterparty]}>Counterparty</Text>
      <Text style={[styles.th, INVOICE_COLS.direction]}>Direction</Text>
      <Text style={[styles.th, INVOICE_COLS.amount, styles.right]}>Amount</Text>
      <Text style={[styles.th, INVOICE_COLS.due]}>Due</Text>
      <Text style={[styles.th, INVOICE_COLS.status]}>Status</Text>
    </View>
  );
}

export async function generateInvoicesPdf(
  options: GenerateInvoicesPdfOptions,
): Promise<Blob> {
  const { invoices, orgName, dateRange } = options;

  const rows = invoices.map((i) => ({
    issued: i.issuedOn,
    counterparty: i.counterparty,
    direction: i.direction,
    amount: `${centsToDisplay(i.amountCents)} ${i.currency}`,
    due: i.dueOn ?? "",
    status: i.status,
  }));

  const pages = chunk(rows, ROWS_PER_PAGE);

  const doc = (
    <Document
      title={`Proofly invoices ${describeDateRange(dateRange.from, dateRange.to)}`}
      author="Proofly"
      creator="Proofly"
      producer="Proofly"
    >
      {pages.map((pageRows, pageIdx) => (
        <Page key={pageIdx} size="A4" style={styles.page} wrap>
          {pageIdx === 0 ? (
            <View style={styles.header}>
              <Text style={styles.headerTitle}>{orgName || "Proofly"}</Text>
              <Text style={styles.headerSub}>Invoice Report</Text>
              <Text style={styles.headerMeta}>
                {describeDateRange(dateRange.from, dateRange.to)} · {rows.length}{" "}
                invoice{rows.length === 1 ? "" : "s"}
              </Text>
            </View>
          ) : null}
          <InvoicesTableHeader />
          {pageRows.length === 0 ? (
            <Text style={styles.emptyState}>
              No invoices matched the selected filters.
            </Text>
          ) : (
            pageRows.map((row, idx) => (
              <View key={idx} style={styles.tableRow} wrap={false}>
                <Text style={[styles.td, INVOICE_COLS.issued]}>
                  {row.issued}
                </Text>
                <Text style={[styles.td, INVOICE_COLS.counterparty]}>
                  {row.counterparty}
                </Text>
                <Text style={[styles.td, INVOICE_COLS.direction]}>
                  {row.direction}
                </Text>
                <Text style={[styles.td, INVOICE_COLS.amount, styles.right]}>
                  {row.amount}
                </Text>
                <Text style={[styles.td, INVOICE_COLS.due]}>{row.due}</Text>
                <Text style={[styles.td, INVOICE_COLS.status]}>
                  {row.status}
                </Text>
              </View>
            ))
          )}
          <PageFooter />
        </Page>
      ))}
    </Document>
  );

  return pdf(doc).toBlob();
}

// Pure filter used by both the UI preview and the export store. Centralising
// this keeps the previewCounts numbers honest against what the store actually
// emits.
export function filterExpensesForExport(
  expenses: Expense[],
  params: {
    dateFrom: string | null | undefined;
    dateTo: string | null | undefined;
    categoryIds?: string[] | null;
    userId?: string | null;
  },
): Expense[] {
  const categoryFilter = params.categoryIds?.length
    ? new Set(params.categoryIds)
    : null;
  return expenses.filter((e) => {
    if (params.dateFrom && e.occurredOn < params.dateFrom) return false;
    if (params.dateTo && e.occurredOn > params.dateTo) return false;
    if (params.userId && e.submittedBy !== params.userId) return false;
    if (categoryFilter) {
      if (!e.categoryId || !categoryFilter.has(e.categoryId)) return false;
    }
    return true;
  });
}

export function filterInvoicesForExport(
  invoices: Invoice[],
  params: {
    dateFrom: string | null | undefined;
    dateTo: string | null | undefined;
    categoryIds?: string[] | null;
    userId?: string | null;
  },
): Invoice[] {
  // Mirrors the CSV builder: if categories were selected, invoices are
  // excluded entirely — categories are an expense-only concept.
  if (params.categoryIds?.length) return [];
  return invoices.filter((i) => {
    if (params.dateFrom && i.issuedOn < params.dateFrom) return false;
    if (params.dateTo && i.issuedOn > params.dateTo) return false;
    if (params.userId && i.submittedBy !== params.userId) return false;
    return true;
  });
}
